import { useState, useRef, useEffect, useCallback } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Pill, ShieldCheck, Eraser, CheckCircle } from '@phosphor-icons/react';
import SignatureCanvas from 'react-signature-canvas';
import { OTC_MEDICATION_TEXT, OTC_MEDICATIONS } from './constants';

export function MedicationForm({ formData, handleInputChange }) {
  const sigRef = useRef(null);
  const [agreed, setAgreed] = useState(false);

  const meds = formData.otc_medications_provided || [];

  const toggleMed = (val) => {
    const current = [...meds];
    const idx = current.indexOf(val);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(val);
    handleInputChange('otc_medications_provided', current);
  };

  const handleSigEnd = useCallback(() => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      handleInputChange('otc_signature', sigRef.current.toDataURL());
    }
  }, [handleInputChange]);

  // Restore signature from formData
  useEffect(() => {
    if (formData.otc_signature && sigRef.current) {
      const timer = setTimeout(() => {
        try {
          if (sigRef.current && sigRef.current.isEmpty()) {
            sigRef.current.fromDataURL(formData.otc_signature, { ratio: 1 });
          }
        } catch { /* canvas not ready */ }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [formData.otc_signature]);

  const clearSig = () => {
    sigRef.current?.clear();
    handleInputChange('otc_signature', '');
  };

  return (
    <div className="space-y-6">
      {/* Patient Info */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl flex items-center gap-2">
            <Pill size={24} className="text-blue-600" />
            OTC Medication — Patient Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">First Name *</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                placeholder="First Name"
                className="rounded-sm border-slate-300"
                data-testid="med-firstname-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Last Name *</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                placeholder="Last Name"
                className="rounded-sm border-slate-300"
                data-testid="med-lastname-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date of Birth</Label>
              <Input
                type="date"
                value={formData.dob}
                onChange={(e) => handleInputChange('dob', e.target.value)}
                className="rounded-sm border-slate-300"
                data-testid="med-dob-input"
              />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Allergies</Label>
            <Input
              value={formData.allergies}
              onChange={(e) => handleInputChange('allergies', e.target.value)}
              placeholder="List any known allergies (especially to Aspirin, Acetaminophen, Ibuprofen)"
              className="rounded-sm border-slate-300"
              data-testid="med-allergies-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Medication Provided */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl flex items-center gap-2">
            <Pill size={24} className="text-green-600" />
            Medication Provided
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {OTC_MEDICATIONS.map((med) => (
              <label
                key={med.value}
                className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer transition-all ${
                  meds.includes(med.value)
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`med-checkbox-${med.value}`}
              >
                <Checkbox
                  checked={meds.includes(med.value)}
                  onCheckedChange={() => toggleMed(med.value)}
                />
                <span className="text-sm font-medium">{med.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Other Medication (if applicable)</Label>
            <Input
              value={formData.otc_other_medication || ''}
              onChange={(e) => handleInputChange('otc_other_medication', e.target.value)}
              placeholder="Specify other OTC medication if not listed above"
              className="rounded-sm border-slate-300"
              data-testid="med-other-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Disclaimer & Signature */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl flex items-center gap-2">
            <ShieldCheck size={24} className="text-amber-600" />
            OTC Medication Acknowledgment & Release of Liability
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-50 p-4 rounded-sm text-sm text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto border border-slate-200" data-testid="otc-disclaimer-text">
            {OTC_MEDICATION_TEXT}
          </div>

          {/* I Agree checkbox */}
          <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50">
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => {
                setAgreed(v);
                handleInputChange('otc_agreed', v);
              }}
              data-testid="otc-agree-checkbox"
            />
            <div>
              <span className="font-semibold text-sm">I Agree</span>
              <p className="text-xs text-slate-500">I have read, understand, and voluntarily agree to the terms contained herein.</p>
            </div>
          </label>

          {/* Signature */}
          {agreed && (
            <div className="space-y-3 animate-in fade-in duration-200">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Patient / Representative Signature</Label>
              {formData.otc_signature && (
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle size={14} className="text-green-600" />
                  <span className="text-xs text-green-600 font-medium">Signature captured</span>
                </div>
              )}
              <div className="border border-slate-300 rounded-sm bg-white">
                <SignatureCanvas
                  ref={sigRef}
                  penColor="black"
                  onEnd={handleSigEnd}
                  canvasProps={{
                    className: 'w-full h-32',
                    'data-testid': 'otc-signature-canvas'
                  }}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearSig}
                className="rounded-sm"
                data-testid="otc-clear-signature"
              >
                <Eraser size={16} className="mr-2" /> Clear Signature
              </Button>
              <div className="space-y-2 mt-3">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</Label>
                <Input
                  type="date"
                  value={formData.otc_date || ''}
                  onChange={(e) => handleInputChange('otc_date', e.target.value)}
                  className="rounded-sm border-slate-300 w-48"
                  data-testid="otc-date-input"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
